<template>
  <main id="quote" class="page-container" v-if="$route.name === 'quotes'">
    <!-- Messaggi -->
    <!-- <p
      v-if="errorMessage"
      :class="{ 'error-message': true, show: errorMessage }"
    >
      {{ errorMessage }}
    </p>
    <p
      v-if="successMessage"
      :class="{ 'success-message': true, show: successMessage }"
    >
      {{ successMessage }}
    </p> -->

    <div class="table-container-quote">
      <!-- LOADER OVERLAY -->
      <div
        v-if="isLoading"
        class="loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="spinner" role="status"></div>
        <p>Carico i preventivi…</p>
      </div>

      <!-- Toolbar -->
      <div class="quotes-toolbar">
        <router-link to="/home/preventivi/nuovo" class="primary-btn">
          <i class="fa-solid fa-plus"></i> Nuovo preventivo
        </router-link>
        <button
          class="refresh-btn"
          :class="{ refreshed }"
          @click="handleRefresh"
          :disabled="refreshing"
          title="Aggiorna"
        >
          <i
            :class="[
              'fa-solid',
              refreshed ? 'fa-check' : 'fa-arrows-rotate',
              { 'fa-spin': refreshing },
            ]"
          ></i>
          <span>{{
            refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
          }}</span>
        </button>
      </div>

      <!-- Tabella -->
      <div class="tbl-wrap" :class="{ dimmed: isLoading }">
        <table class="tbl quote-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Totale da pagare</th>
              <th>Data</th>
              <th>Indirizzo di consegna</th>
              <th>Conferma</th>
              <th>Azioni</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="quote in allQuotes"
              :key="quote.id"
              class="quote-row"
              :class="{ 'quote-row--confirmed': quote.isConfirmed }"
            >
              <!-- <td>{{ quote.Client.clientName }}</td> -->
              <td
                class="client-cell"
                @mouseenter="onCellEnter(quote.id)"
                @mouseleave="onCellLeave(quote.id)"
              >
                <button
                  class="client-trigger"
                  @focus="onCellEnter(quote.id)"
                  @blur="onCellLeave(quote.id)"
                  @click.stop.prevent="toggleTooltip(quote.id)"
                  @touchstart.stop.prevent="onTriggerTouch(quote.id)"
                  @keydown="onTriggerKeydown($event, quote.id)"
                  :aria-expanded="isTooltipVisible(quote.id)"
                  :aria-describedby="'tt-' + quote.id"
                  type="button"
                >
                  {{ quote.Client?.clientName || "—" }}
                  <span class="sr-only">Apri contatti</span>
                  <span class="client-hint-mobile">Tocca per contatti</span>
                </button>

                <transition name="fade">
                  <div
                    v-if="isTooltipVisible(quote.id)"
                    :id="'tt-' + quote.id"
                    role="tooltip"
                    class="email-tooltip"
                    :class="{ 'no-pointer': lockClickId === quote.id }"
                    @click.stop
                    @touchstart.stop
                  >
                    <!-- CONTENUTO TOOLTIP -->
                    <div class="tooltip-row" v-if="quote?.Client?.email">
                      <span class="label">Email</span>
                      <a :href="'mailto:' + quote.Client.email">{{
                        quote.Client.email
                      }}</a>
                      <button
                        class="copy-btn"
                        @click="copyToClipboard(quote.Client.email, 'email')"
                        title="Copia email"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>

                    <div class="tooltip-row" v-if="quote?.Client?.pec">
                      <span class="label">PEC</span>
                      <a :href="'mailto:' + quote.Client.pec">{{
                        quote.Client.pec
                      }}</a>
                      <button
                        class="copy-btn"
                        @click="copyToClipboard(quote.Client.pec, 'pec')"
                        title="Copia PEC"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>

                    <div class="tooltip-row" v-if="quote?.Client?.phoneNumber">
                      <span class="label">Telefono</span>
                      {{ quote.Client.phoneNumber }}
                      <button
                        class="copy-btn"
                        @click="
                          copyToClipboard(quote.Client.phoneNumber, 'phone')
                        "
                        title="Copia numero di telefono"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>

                    <div
                      class="tooltip-row muted"
                      v-if="
                        !quote?.Client?.email &&
                        !quote?.Client?.pec &&
                        !quote?.Client?.phoneNumber
                      "
                    >
                      Nessun contatto disponibile.
                    </div>
                  </div>
                </transition>
              </td>

              <td>{{ formatPriceToPrint(quote.realTotalPrice) }}</td>
              <td class="date-cell">
                {{ formatToLocalDate(quote.date) }}
              </td>
              <td class="address-cell">
                <a
                  v-if="quote.location && quote.location.link"
                  :href="quote.location.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="custom-link"
                  >{{ formatAddress(quote.location.address) }}</a
                >
                <span v-else>{{
                  formatAddress(
                    quote.location?.address || "Indirizzo non disponibile"
                  )
                }}</span>
              </td>

              <!-- Conferma -->
              <td class="state-container">
                <StatusSelect
                  :modelValue="quote.isConfirmed"
                  :options="CONFERMA_OPTIONS"
                  :disabled="Boolean(quote.isConfirmed)"
                  @update:modelValue="
                    (val) => {
                      quote.isConfirmed = val;
                      onQuoteStateChange(quote, 'isConfirmed', val);
                    }
                  "
                />
              </td>

              <!-- Azioni -->
              <td class="actions-cell">
                <template v-if="!quote.isConfirmed">
                  <router-link
                    v-if="!showArchived"
                    :to="`/home/preventivi/dettaglio/${quote.id}`"
                    class="icons-button"
                    title="Visualizza preventivo"
                    aria-label="Visualizza preventivo"
                  >
                    <i class="fa-solid fa-eye"></i>
                  </router-link>

                  <button
                    v-if="!showArchived"
                    class="icons-button icons-button--danger"
                    @click="openArchiveModal(quote)"
                    title="Archivia preventivo"
                    aria-label="Archivia preventivo"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                  </button>

                  <button
                    v-else-if="canRestore(quote)"
                    class="icons-button icons-button--danger"
                    @click="openRestoreModal(quote)"
                    title="Ripristina preventivo"
                    aria-label="Ripristina preventivo"
                  >
                    <i class="fa-solid fa-trash-arrow-up"></i>
                  </button>
                </template>

                <template v-else>
                  <router-link
                    to="/home/ordini"
                    class="vai-ordine-btn"
                    title="Vai all'ordine"
                    aria-label="Vai all'ordine"
                  >
                    <i class="fa-solid fa-clipboard-list"></i>
                    <span>Vai all'ordine</span>
                  </router-link>
                </template>
              </td>
            </tr>
            <tr v-if="allQuotes.length === 0 && !isLoading">
              <td colspan="6" class="no-result">Nessun preventivo trovato.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination / search / archived toggle -->
      <div class="pagination">
        <div class="pagination-left">
          <div class="search-container">
            <i
              v-if="!searchTerm"
              class="fa-solid fa-magnifying-glass search-icon"
            ></i>
            <input
              type="text"
              v-model="searchTerm"
              @keyup.enter="onSearch"
              placeholder="Cerca preventivi..."
              class="search-box"
            />

            <button
              v-if="searchTerm"
              @click="onResetSearch"
              class="clear-search-button"
              :disabled="isLoading"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>

          <label v-if="hasArchived" class="archived-toggle tooltip">
            <input
              type="checkbox"
              v-model="showArchived"
              @change="onToggleArchived($event)"
              class="visually-hidden"
              :aria-label="
                showArchived
                  ? 'Torna ai preventivi attivi'
                  : 'Mostra preventivi archiviati'
              "
            />
            <i
              :class="[
                'fa-solid',
                showArchived ? 'fa-file-invoice' : 'fa-box-archive',
                'icons-button',
              ]"
              aria-hidden="true"
            ></i>
            <span class="tooltiptext">
              {{
                showArchived
                  ? "Torna ai preventivi attivi"
                  : "Mostra preventivi archiviati"
              }}
            </span>
          </label>
        </div>

        <div class="pagination-right">
          <div class="pagination-info">
            {{ pagination.startItem }}-{{ pagination.endItem }} di
            {{ pagination.totalItems }}
          </div>

          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="isLoading || pagination.currentPage === 1"
              :title="
                isLoading || pagination.currentPage === 1
                  ? null
                  : 'Torna alla prima pagina'
              "
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="isLoading || pagination.currentPage === 1"
              :title="
                isLoading || pagination.currentPage === 1
                  ? null
                  : 'Torna alla pagina precedente'
              "
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>

            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: pagination.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>

            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
              :title="
                isLoading || pagination.currentPage === pagination.totalPages
                  ? null
                  : 'Vai alla pagina successiva'
              "
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.totalPages)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
              :title="
                isLoading || pagination.currentPage === pagination.totalPages
                  ? null
                  : `Vai all'ultima pagina`
              "
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- MODALE DOCUMENTI -->
      <VueFinalModal
        v-model="showDocumentsModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeDocumentsModal"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">
            {{
              docModalSection === "ddt" ? "Storico DDT" : "Documenti preventivo"
            }}
            #{{ currentQuoteId }}
          </h3>

          <!-- Documenti preventivo (PDF) -->
          <template v-if="docModalSection === 'pdf'">
            <transition-group name="card" tag="div" class="documents-grid">
              <div
                v-for="(doc, i) in filteredDocuments"
                :key="safeDocKey(doc, i)"
                class="document-card"
              >
                <a :href="doc.url" target="_blank" class="card-link">
                  <div class="card-icon-pdf">
                    <i class="fa-solid fa-file-pdf"></i>
                  </div>
                  <div class="card-body">
                    <div class="card-title">{{ formatDocName(doc.name) }}</div>
                    <div class="card-subtitle">
                      Data di creazione: {{ formatToLocalDate(doc.createdAt) }}
                    </div>
                    <div
                      v-if="
                        doc._quoteUpdatedAt &&
                        formatToLocalDateAndHours(doc._quoteUpdatedAt) !==
                          formatToLocalDateAndHours(doc.createdAt)
                      "
                      class="card-subtitle"
                    >
                      Aggiornato il:
                      {{ formatToLocalDateAndHours(doc._quoteUpdatedAt) }}
                    </div>
                  </div>
                </a>
                <button
                  class="icons-button card-email-btn"
                  :class="{ 'email-sent': emailSentMap[selectedQuote?.id] }"
                  :title="
                    emailSentMap[selectedQuote?.id]
                      ? 'Email già inviata in questa sessione'
                      : 'Invia email al cliente'
                  "
                  aria-label="Invia email al cliente"
                  @click.stop="onSendQuoteEmail(selectedQuote)"
                >
                  <i class="fa-solid fa-envelope"></i>
                </button>
              </div>

              <div v-if="filteredDocuments.length === 0" class="no-result">
                Nessun documento trovato
              </div>
            </transition-group>
          </template>

          <!-- Storico DDT -->
          <template v-if="docModalSection === 'ddt'">
            <div class="ddt-history">
              <div v-if="selectedDdtDocuments.length === 0" class="no-result">
                Nessun DDT trovato per questo preventivo.
              </div>
              <template v-if="ddtDocumentsByCausale.consegna.length > 0">
                <div class="ddt-history__group-label">
                  Consegne
                  <span class="ddt-history__group-count"
                    >({{ ddtDocumentsByCausale.consegna.length }})</span
                  >
                </div>
                <div
                  v-for="(doc, idx) in ddtDocumentsByCausale.consegna"
                  :key="doc.id"
                  class="ddt-history__row"
                >
                  <a :href="doc.url" target="_blank" class="ddt-history__link">
                    <i class="fa-solid fa-file-arrow-down"></i>
                    DDT N° {{ doc._ddtId }}
                    <span class="ddt-history__seq">Consegna {{ idx + 1 }}</span>
                  </a>
                  <span class="ddt-history__date">{{
                    formatToLocalDate(doc.createdAt)
                  }}</span>
                </div>
              </template>
              <template v-if="ddtDocumentsByCausale.reso.length > 0">
                <div class="ddt-history__group-label">Resi</div>
                <div
                  v-for="doc in ddtDocumentsByCausale.reso"
                  :key="doc.id"
                  class="ddt-history__row"
                >
                  <a :href="doc.url" target="_blank" class="ddt-history__link">
                    <i class="fa-solid fa-file-arrow-down"></i>
                    DDT N° {{ doc._ddtId }}
                  </a>
                  <span class="ddt-history__date">{{
                    formatToLocalDate(doc.createdAt)
                  }}</span>
                </div>
              </template>
              <template v-if="ddtDocumentsByCausale.ritiro.length > 0">
                <div class="ddt-history__group-label">Ritiri</div>
                <div
                  v-for="doc in ddtDocumentsByCausale.ritiro"
                  :key="doc.id"
                  class="ddt-history__row"
                >
                  <a :href="doc.url" target="_blank" class="ddt-history__link">
                    <i class="fa-solid fa-file-arrow-down"></i>
                    DDT N° {{ doc._ddtId }}
                  </a>
                  <span class="ddt-history__date">{{
                    formatToLocalDate(doc.createdAt)
                  }}</span>
                </div>
              </template>
            </div>
          </template>
        </template>
      </VueFinalModal>

      <!-- MODALE ARCHIVIAZIONE -->
      <VueFinalModal
        v-model="showArchiveModal"
        :clickToClose="!archiving"
        :escToClose="!archiving"
        classes="documents-modal"
        content-class="confirm-modal-content modal--archive"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeArchiveModal"
            :disabled="archiving"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">Archivia preventivo</h3>
          <p class="modal__subtitle">
            Archiviare il preventivo
            <strong>#{{ currentQuoteId }}</strong>
            di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong
            >?
          </p>

          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeArchiveModal"
              :disabled="archiving"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmArchive"
              :disabled="archiving"
            >
              <span v-if="archiving">Archivia…</span>
              <span v-else>Archivia</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE RIPRISTINO -->
      <VueFinalModal
        v-model="showRestoreModal"
        :clickToClose="!restoring"
        :escToClose="!restoring"
        classes="documents-modal"
        content-class="confirm-modal-content modal--restore"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeRestoreModal"
            :disabled="restoring"
            title="Chiudi"
            aria-label="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">Ripristina preventivo</h3>
          <p class="modal__subtitle">
            Ripristinare il preventivo
            <strong>#{{ currentQuoteId }}</strong>
            di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong> dagli
            archivi?
          </p>

          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeRestoreModal"
              :disabled="restoring"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmRestore"
              :disabled="restoring"
            >
              <span v-if="restoring">Ripristino…</span>
              <span v-else>Ripristina</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE CONFERMA CAMBIO STATO -->
      <VueFinalModal
        v-model="showConfirmModal"
        :clickToClose="!confirmBusy"
        :escToClose="!confirmBusy"
        classes="documents-modal"
        content-class="confirm-modal-content modal--state"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="handleConfirmCancel"
            :disabled="confirmBusy"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">{{ confirmTitle }}</h3>
          <p class="modal__subtitle" v-html="confirmMessage"></p>

          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="handleConfirmCancel"
              :disabled="confirmBusy"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="handleConfirmOK"
              :disabled="confirmBusy"
            >
              <span v-if="confirmBusy">{{ confirmBusyLabel }}</span>
              <span v-else>{{ confirmLabel }}</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE INSERIMENTO LISTINO O ACCONTO -->
      <VueFinalModal
        v-model="showDiscountModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content"
      >
        <template #default>
          <button class="modal__close icons-button" @click="closeDiscountModal">
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">Preventivo #{{ currentQuoteId }}</h3>

          <transition-group name="card" tag="div" class="documents-grid">
            <div
              v-for="(quote, i) in allQuotes.filter(
                (q) => q.id === currentQuoteId
              )"
              :key="safeDocKey(quote, i)"
              class="quote-modifier"
            >
              <!-- Modificatori già presenti -->
              <div
                class="current-modifiers"
                v-if="
                  (quote.discount_percent ?? 0) > 0 ||
                  (quote.deposit ?? 0) > 0 ||
                  (quote.surcharge_percent ?? 0) > 0
                "
              >
                <div
                  v-if="(quote.discount_percent ?? 0) > 0"
                  class="modifier-row"
                >
                  <p>
                    <strong>Decremento prezzo applicato: </strong>
                    - {{ Number(quote.discount_percent).toFixed(0) }}%
                    <span v-if="(quote.discount ?? 0) > 0">
                      ({{ formatPriceToPrint(quote.discount) }})
                    </span>
                  </p>
                  <button
                    class="modifier-remove-btn"
                    type="button"
                    title="Rimuovi decremento prezzo"
                    :disabled="removeModifierBusy"
                    @click="removeModifier(quote, 'discount_percent')"
                  >
                    <i class="fa-solid fa-trash-can"></i> Rimuovi
                  </button>
                </div>
                <div
                  v-if="(quote.surcharge_percent ?? 0) > 0"
                  class="modifier-row"
                >
                  <p>
                    <strong>Incremento prezzo applicato: </strong>
                    + {{ Number(quote.surcharge_percent).toFixed(0) }}%
                    <span v-if="(quote.surcharge ?? 0) > 0">
                      ({{ formatPriceToPrint(quote.surcharge) }})
                    </span>
                  </p>
                  <button
                    class="modifier-remove-btn"
                    type="button"
                    title="Rimuovi incremento prezzo"
                    :disabled="removeModifierBusy"
                    @click="removeModifier(quote, 'surcharge_percent')"
                  >
                    <i class="fa-solid fa-trash-can"></i> Rimuovi
                  </button>
                </div>
                <div v-if="(quote.deposit ?? 0) > 0" class="modifier-row">
                  <p>
                    <strong>Acconto applicato: </strong>
                    - {{ formatPriceToPrint(quote.deposit) }}
                  </p>
                  <button
                    class="modifier-remove-btn"
                    type="button"
                    title="Rimuovi acconto"
                    :disabled="removeModifierBusy"
                    @click="removeModifier(quote, 'deposit')"
                  >
                    <i class="fa-solid fa-trash-can"></i> Rimuovi
                  </button>
                </div>
                <p class="hint">
                  Vuoi applicare un'ulteriore modifica al prezzo?
                </p>
              </div>

              <!-- Wizard form applicazione -->
              <div class="wizard-modifier">
                <!-- Step indicator -->
                <div class="wiz-steps">
                  <div
                    class="wiz-step"
                    :class="{ active: wizardStep === 1, done: wizardStep > 1 }"
                  >
                    <div class="wiz-step-dot">
                      <i v-if="wizardStep > 1" class="fa-solid fa-check"></i>
                      <span v-else>1</span>
                    </div>
                    <span class="wiz-step-label">Modifica</span>
                  </div>
                  <div
                    class="wiz-connector"
                    :class="{ done: wizardStep > 1 }"
                  ></div>
                  <div class="wiz-step" :class="{ active: wizardStep === 2 }">
                    <div class="wiz-step-dot"><span>2</span></div>
                    <span class="wiz-step-label">Acconto</span>
                  </div>
                </div>

                <!-- STEP 1: Scelta tipo + percentuale -->
                <div v-if="wizardStep === 1" class="wiz-panel">
                  <p class="wiz-hint">
                    Scegli la modifica da applicare al preventivo
                  </p>

                  <div class="wiz-type-cards">
                    <button
                      class="wiz-type-card"
                      :class="{ selected: modifierType === 'discount_percent' }"
                      type="button"
                      @click="
                        modifierType = 'discount_percent';
                        surchargePercent = 0;
                      "
                    >
                      <span class="wiz-card-icon"
                        ><i class="fa-solid fa-tag"></i
                      ></span>
                      <span class="wiz-badge wiz-badge--cumul"
                        >Decrementa prezzo</span
                      >
                      <span class="wiz-card-desc">Riduzione %</span>
                    </button>
                    <button
                      class="wiz-type-card"
                      :class="{
                        selected: modifierType === 'surcharge_percent',
                      }"
                      type="button"
                      @click="
                        modifierType = 'surcharge_percent';
                        discountPercent = 0;
                      "
                    >
                      <span class="wiz-card-icon"
                        ><i class="fa-solid fa-arrow-trend-up"></i
                      ></span>
                      <span class="wiz-badge wiz-badge--single"
                        >Incrementa prezzo</span
                      >
                      <span class="wiz-card-desc">Incremento %</span>
                    </button>
                  </div>

                  <div
                    v-if="
                      modifierType === 'discount_percent' ||
                      modifierType === 'surcharge_percent'
                    "
                    class="wiz-input-row"
                  >
                    <label class="wiz-label">
                      {{
                        modifierType === "discount_percent"
                          ? "Percentuale riduzione"
                          : "Percentuale incremento"
                      }}
                    </label>
                    <div class="wiz-input-wrap">
                      <input
                        v-if="modifierType === 'discount_percent'"
                        type="number"
                        v-model.number="discountPercent"
                        min="0"
                        max="100"
                        step="1"
                        placeholder="0"
                        class="wiz-input"
                      />
                      <input
                        v-else
                        type="number"
                        v-model.number="surchargePercent"
                        min="0"
                        step="1"
                        placeholder="0"
                        class="wiz-input"
                      />
                      <span class="wiz-input-suffix">%</span>
                    </div>
                  </div>

                  <button
                    class="wiz-btn wiz-btn--primary"
                    type="button"
                    :disabled="!canAdvanceStep1()"
                    @click="wizardStep = 2"
                  >
                    Avanti <i class="fa-solid fa-arrow-right"></i>
                  </button>
                </div>

                <!-- STEP 2: Preview + acconto opzionale -->
                <div v-else-if="wizardStep === 2" class="wiz-panel">
                  <!-- Preview dalla step 1 -->
                  <div class="wiz-preview preview">
                    <div
                      v-if="modifierType === 'discount_percent'"
                      class="breakdown"
                    >
                      <div class="title">
                        Anteprima riduzione
                        <span class="wiz-badge wiz-badge--cumul"
                          >Decrementa prezzo</span
                        >
                      </div>
                      <ul class="calc-list">
                        <li>
                          Totale preventivo:
                          <strong>{{
                            formatPriceToPrint(Number(quote.totalPrice) || 0)
                          }}</strong>
                        </li>
                        <li v-if="(Number(quote.deposit) || 0) > 0">
                          Acconto già applicato:
                          <strong
                            >-
                            {{
                              formatPriceToPrint(Number(quote.deposit) || 0)
                            }}</strong
                          >
                        </li>
                        <li>
                          Riduzione - ({{
                            Number(discountPercent).toFixed(0)
                          }}%):
                          <strong>
                            {{
                              formatPriceToPrint(
                                Math.max(
                                  0,
                                  Math.max(
                                    0,
                                    (Number(quote.totalPrice) || 0) -
                                      (Number(quote.deposit) || 0)
                                  ) *
                                    (Number(discountPercent) / 100)
                                )
                              )
                            }}</strong
                          >
                        </li>
                        <li class="row-highlight">
                          Nuovo totale:
                          <span class="calc-final">
                            <strong>{{
                              formatPriceToPrint(
                                Math.max(
                                  0,
                                  Math.max(
                                    0,
                                    (Number(quote.totalPrice) || 0) -
                                      (Number(quote.deposit) || 0)
                                  ) *
                                    (1 - Number(discountPercent) / 100)
                                )
                              )
                            }}</strong>
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div
                      v-else-if="modifierType === 'surcharge_percent'"
                      class="breakdown"
                    >
                      <div class="title">
                        Anteprima incremento
                        <span class="wiz-badge wiz-badge--single"
                          >Incrementa prezzo</span
                        >
                      </div>
                      <ul class="calc-list">
                        <li>
                          Totale preventivo:
                          <strong>{{
                            formatPriceToPrint(Number(quote.totalPrice) || 0)
                          }}</strong>
                        </li>
                        <li>
                          Incremento + ({{
                            Number(surchargePercent).toFixed(0)
                          }}%):
                          <strong>
                            {{
                              formatPriceToPrint(
                                Math.round(
                                  ((Number(quote.totalPrice) || 0) *
                                    (Number(surchargePercent) / 100) +
                                    Number.EPSILON) *
                                    100
                                ) / 100
                              )
                            }}</strong
                          >
                        </li>
                        <li class="row-highlight">
                          Nuovo totale:
                          <span class="calc-final">
                            <strong>{{
                              formatPriceToPrint(
                                Math.round(
                                  ((Number(quote.totalPrice) || 0) *
                                    (1 + Number(surchargePercent) / 100) +
                                    Number.EPSILON) *
                                    100
                                ) / 100
                              )
                            }}</strong>
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Acconto opzionale -->
                  <div class="wiz-optional">
                    <p class="wiz-optional-label">
                      <i class="fa-solid fa-circle-info"></i>
                      Acconto (opzionale)
                    </p>
                    <div class="wiz-input-wrap">
                      <span class="wiz-input-prefix">€</span>
                      <input
                        type="number"
                        v-model.number="depositAmount"
                        min="0"
                        step="1"
                        placeholder="0"
                        class="wiz-input"
                      />
                    </div>
                    <div
                      v-if="(Number(depositAmount) || 0) > 0"
                      class="preview wiz-deposit-preview"
                    >
                      <div class="breakdown">
                        <div class="title">Con acconto</div>
                        <ul class="calc-list">
                          <li>
                            Subtotale dopo modifica:
                            <strong>{{
                              formatPriceToPrint(wizardSubtotal(quote))
                            }}</strong>
                          </li>
                          <li>
                            Acconto:
                            <strong
                              >-
                              {{
                                formatPriceToPrint(Number(depositAmount) || 0)
                              }}</strong
                            >
                          </li>
                          <li class="row-highlight">
                            Rimanente:
                            <span class="calc-final">
                              <strong>{{
                                formatPriceToPrint(
                                  Math.max(
                                    0,
                                    wizardSubtotal(quote) -
                                      (Number(depositAmount) || 0)
                                  )
                                )
                              }}</strong>
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div class="wiz-actions">
                    <button
                      class="wiz-btn wiz-btn--ghost"
                      type="button"
                      @click="wizardStep = 1"
                    >
                      <i class="fa-solid fa-arrow-left"></i> Indietro
                    </button>
                    <button
                      class="wiz-btn wiz-btn--cta"
                      type="button"
                      :disabled="confirmBusy"
                      @click="applyWizard(quote)"
                    >
                      <span v-if="confirmBusy">Salvo…</span>
                      <span v-else
                        >Applica <i class="fa-solid fa-check"></i
                      ></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- (resto del grid dei documenti, invariato) -->
            <div
              v-for="(doc, i) in filteredDocuments"
              :key="safeDocKey(doc, i)"
              class="document-card"
            >
              <a :href="doc.url" target="_blank" class="card-link">
                <div class="card-icon-pdf">
                  <i class="fa-solid fa-file-pdf"></i>
                </div>
                <div class="card-body">
                  <div class="card-title">{{ formatDocName(doc.name) }}</div>
                  <div class="card-subtitle">
                    Data di creazione: {{ formatToLocalDate(doc.createdAt) }}
                  </div>
                </div>
              </a>
            </div>
            <div v-if="filteredDocuments.length === 0" class="no-result">
              Nessun documento trovato
            </div>
          </transition-group>
        </template>
      </VueFinalModal>
    </div>

    <DdtModal
      :show="showDdtModal"
      :items="ddtModalItems"
      :quote-id="ddtModalQuoteId"
      :client-id="ddtModalClientId"
      :client-name="ddtModalClientName"
      @update:show="showDdtModal = $event"
    />
  </main>
  <router-view></router-view>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import { generateQuotePdf } from "@/utilities/generateQuotePdf";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import {
  formatPriceToPrint,
  formatToLocalDate,
  formatToLocalDateAndHours,
} from "../utilities/formatting";
import DdtModal from "@/components/DdtModal.vue";
import StatusSelect from "@/components/StatusSelect.vue";

const CONFERMA_OPTIONS = [
  { value: false, label: "In attesa", color: "#ffa726" },
  { value: true, label: "Confermato", color: "#4caf50" },
];

const PAGAMENTO_OPTIONS = [
  { value: "In attesa", label: "In attesa", color: "#ffa726" },
  { value: "Pagato", label: "Pagato", color: "#4caf50" },
];

const SPEDIZIONE_COLOR = {
  "In magazzino": "#9ca3af",
  "In consegna": "#ffa726",
  Consegnato: "#4caf50",
};

export default {
  components: { DdtModal, StatusSelect },

  computed: {
    ...mapGetters({
      allQuotes: "quotes/list",
      pagination: "quotes/pagination",
    }),

    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },

    filteredDocuments() {
      const list = Array.isArray(this.selectedDocuments)
        ? this.selectedDocuments
        : [];
      const term = (this.docSearchTerm || "").toLowerCase().trim();
      const seen = new Set();
      return list.filter(Boolean).filter((doc) => {
        const k = doc.id ?? doc.name ?? doc.url ?? Math.random();
        if (seen.has(k)) return false;
        seen.add(k);
        return term ? (doc.name || "").toLowerCase().includes(term) : true;
      });
    },
    ddtDocumentsByCausale() {
      const groups = { consegna: [], reso: [], ritiro: [] };
      for (const doc of this.selectedDdtDocuments) {
        const key = doc._causale in groups ? doc._causale : "consegna";
        groups[key].push(doc);
      }
      const byDate = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);
      return {
        consegna: [...groups.consegna].sort(byDate),
        reso: [...groups.reso].sort(byDate),
        ritiro: [...groups.ritiro].sort(byDate),
      };
    },
  },

  data() {
    return {
      tooltipOpenId: null,
      hoveredId: null,
      hideTimer: null,
      lockClickId: null, // anti-ghost click

      isLoading: false,
      CONFERMA_OPTIONS,
      PAGAMENTO_OPTIONS,

      refreshing: false,
      refreshed: false,
      errorMessage: "",
      successMessage: "",
      currentPage: 1,
      searchTerm: "",
      currentQuoteId: null,
      showDocumentsModal: false,
      docModalSection: "pdf",
      selectedDocuments: [],
      selectedDdtDocuments: [],
      showArchived: false,
      hasArchived: false,
      filterConfirmed: undefined,
      depositAmount: 0,
      wizardStep: 1,
      modifierType: "",
      discountPercent: 0,
      surchargePercent: 0,
      docSearchTerm: "", // per ricerca documenti nella modale

      /* Modali archiviazione / ripristino */
      showArchiveModal: false,
      archiving: false,
      showRestoreModal: false,
      restoring: false,
      selectedQuote: null,

      showConfirmModal: false,
      showDiscountModal: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmLabel: "Conferma",
      confirmBusyLabel: "Aggiorno…",
      confirmBusy: false,
      removeModifierBusy: false,
      emailSentMap: {},
      pendingChange: null, // { id, field, newValue }

      showDdtModal: false,
      ddtModalItems: [],
      ddtModalQuoteId: null,
      ddtModalClientId: null,
      ddtModalClientName: null,
    };
  },

  methods: {
    spedizioneOptions(quote) {
      return this.availableStateOptions(quote).map((opt) => ({
        ...opt,
        color: SPEDIZIONE_COLOR[opt.value] ?? "#9ca3af",
      }));
    },

    formatAddress(address) {
      if (!address) return "Indirizzo non disponibile";
      const withCap = address.replace(/,?\s+(\d{5}\s)/, "\n$1");
      if (withCap !== address) return withCap;
      return address.replace(/,\s*/, ",\n");
    },

    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.getQuotes(this.pagination.currentPage);
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    availableStateOptions(quote) {
      const s = quote.state;
      if (s === "In magazzino") {
        return [
          { value: "In magazzino", label: "In magazzino", disabled: true },
          { value: "In consegna", label: "In consegna", disabled: false },
        ];
      }
      if (s === "In consegna") {
        return [
          { value: "In consegna", label: "In consegna", disabled: true },
          { value: "Consegnato", label: "Consegnato", disabled: false },
        ];
      }
      return [
        { value: s || "Consegnato", label: s || "Consegnato", disabled: true },
      ];
    },

    isStateSelectDisabled(quote) {
      if (quote.state === "Consegnato") return true;
      if (quote.state === "In magazzino") {
        return !quote.isConfirmed || quote.payment_state !== "Pagato";
      }
      return !quote.isConfirmed;
    },

    canApplyModifier() {
      if (this.modifierType === "discount_percent") {
        return this.discountPercent > 0 && this.discountPercent <= 100;
      }
      if (this.modifierType === "surcharge_percent") {
        return this.surchargePercent > 0;
      }
      if (this.modifierType === "deposit") {
        return this.depositAmount > 0;
      }
      return false;
    },
    canAdvanceStep1() {
      if (this.modifierType === "discount_percent") {
        return this.discountPercent > 0 && this.discountPercent <= 100;
      }
      if (this.modifierType === "surcharge_percent") {
        return this.surchargePercent > 0;
      }
      return false;
    },
    wizardSubtotal(quote) {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      if (this.modifierType === "discount_percent") {
        const base = Number(quote.totalPrice) || 0;
        const existingDeposit = Number(quote.deposit) || 0;
        const taxable = Math.max(0, base - existingDeposit);
        return Math.max(
          0,
          round2(taxable * (1 - Number(this.discountPercent) / 100))
        );
      }
      if (this.modifierType === "surcharge_percent") {
        const base = Number(quote.totalPrice) || 0;
        return round2(base * (1 + Number(this.surchargePercent) / 100));
      }
      return Number(quote.totalPrice) || 0;
    },
    async applyWizard(quote) {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      this.confirmBusy = true;
      try {
        if (this.modifierType === "discount_percent") {
          const base = Number(quote.totalPrice) || 0;
          const deposit = Number(quote.deposit) || 0;
          const taxable = Math.max(0, base - deposit);
          const percent = Number(this.discountPercent || 0);
          const amount = round2(taxable * (percent / 100));
          await axiosInstance.post(`quote/priceAdjustment/${quote.id}`, {
            type: "discount_percent",
            percent,
            amount,
          });
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) {
            q.discount_percent = percent;
            q.discount = amount;
          }
        } else if (this.modifierType === "surcharge_percent") {
          const percent = Number(this.surchargePercent || 0);
          const base = Number(quote.totalPrice) || 0;
          const amount = round2(base * (percent / 100));
          await axiosInstance.post(`quote/priceAdjustment/${quote.id}`, {
            type: "surcharge_percent",
            percent,
            amount,
          });
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) {
            q.surcharge_percent = percent;
            q.surcharge = amount;
            q.realTotalPrice = round2(Number(q.totalPrice || 0) + amount);
          }
        }

        if ((Number(this.depositAmount) || 0) > 0) {
          const amount = round2(Number(this.depositAmount));
          await axiosInstance.post(`quote/priceAdjustment/${quote.id}`, {
            type: "deposit",
            amount,
          });
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) q.deposit = round2((Number(q.deposit) || 0) + amount);
        }

        this.$toast.success("Preventivo aggiornato.");

        const qLocal = this.allQuotes.find((x) => x.id === quote.id);
        const needsFetch = !qLocal?.quote_items?.some((qi) => qi?.item);
        const dataForPdf = needsFetch
          ? await this.getQuotebyId(quote.id)
          : qLocal;
        await generateQuotePdf({
          quote: dataForPdf,
          toast: this.$toast,
          uploadQuoteDocument: (payload) =>
            this.$store.dispatch("uploadQuoteDocument", payload),
          onComplete: async () => {
            await this.getQuotes(this.pagination.currentPage);
          },
        });
        this.closeDiscountModal();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            "Errore nel salvataggio."
        );
      } finally {
        this.confirmBusy = false;
      }
    },
    async removeModifier(quote, type) {
      this.removeModifierBusy = true;
      try {
        await axiosInstance.delete(`quote/priceAdjustment/${quote.id}`, {
          data: { type },
        });
        const q = this.allQuotes.find((x) => x.id === quote.id);
        if (q) {
          if (type === "discount_percent") {
            q.discount_percent = 0;
            q.discount = 0;
          } else if (type === "surcharge_percent") {
            q.surcharge_percent = 0;
            q.surcharge = 0;
          } else if (type === "deposit") {
            q.deposit = 0;
          }
        }
        const successMsg =
          type === "discount_percent"
            ? "Decremento prezzo rimosso."
            : type === "surcharge_percent"
            ? "Incremento prezzo rimosso."
            : "Acconto rimosso.";
        this.$toast.success(successMsg);
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nella rimozione."
        );
      } finally {
        this.removeModifierBusy = false;
      }
    },

    openDdtModal(quote) {
      const AREA_CATS = [
        "piastrelle",
        "rivestimenti parete",
        "parquet",
        "laminati",
        "decking esterno",
      ];
      const LINEAR_CATS = ["profili", "sistemi di scarico"];
      const rows = [];
      const seen = new Set();

      (quote.items || []).forEach((it) => {
        seen.add(it.id);
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const qty = it?.QuoteItem?.quantity ?? 0;
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        rows.push({
          description: it.itemName || it.itemCode || "—",
          quantity: qty,
          uom,
        });
      });

      (quote.quote_items || []).forEach((qi) => {
        if (qi?.item?.id && seen.has(qi.item.id)) return;
        const it = qi.item || {};
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        rows.push({
          description: it.itemName || it.itemCode || "—",
          quantity: qi.quantity ?? 0,
          uom,
        });
      });

      this.ddtModalItems = rows;
      this.ddtModalQuoteId = quote.id;
      this.ddtModalClientId = quote.clientId || null;
      this.ddtModalClientName = quote?.Client?.clientName || null;
      this.showDdtModal = true;
    },

    ...mapActions([
      "fetchQuotes",
      "archiveQuote",
      "restoreQuote",
      "hasArchivedQuotes",
      "uploadQuoteDocument",
      "sendQuoteDocumentEmail",
    ]),

    async onSendQuoteEmail(quote) {
      const doc = (quote.documents || []).at(-1);
      if (!doc) return;
      try {
        await this.sendQuoteDocumentEmail({
          quoteId: quote.id,
          objectName: doc.name,
        });
        this.emailSentMap = { ...this.emailSentMap, [quote.id]: true };
        this.$toast.success("Email inviata al cliente con successo.");
      } catch (err) {
        const msg =
          err.response?.data?.error || "Errore durante l'invio dell'email.";
        this.$toast.error(msg);
      }
    },

    isTooltipVisible(id) {
      return this.tooltipOpenId === id || this.hoveredId === id;
    },
    applyTooltipPlacement(id) {
      const tip = document.getElementById("tt-" + id);
      if (tip) tip.dataset.placement = "over";
    },
    onCellEnter(id) {
      clearTimeout(this.hideTimer);
      this.hoveredId = id;
      this.$nextTick(() => this.applyTooltipPlacement(id));
    },
    onCellLeave(id) {
      clearTimeout(this.hideTimer);
      // piccolo delay per evitare micro-sfarfallii
      this.hideTimer = setTimeout(() => {
        // se non è aperto "sticky" con click, chiudi l'hover
        if (this.tooltipOpenId !== id) this.hoveredId = null;
      }, 120);
    },
    toggleTooltip(id) {
      this.tooltipOpenId = this.tooltipOpenId === id ? null : id;
      if (this.tooltipOpenId === id) {
        this.hoveredId = id;
        this.lockClickId = id; // blocca click subito dopo l’apertura
        setTimeout(() => {
          if (this.lockClickId === id) this.lockClickId = null;
        }, 250); // ~250ms bastano per il ghost click
        this.$nextTick(() => this.applyTooltipPlacement(id));
      }
    },

    onTriggerTouch(id) {
      // equivalente touch del click, con lock sicuro
      this.tooltipOpenId = this.tooltipOpenId === id ? null : id;
      if (this.tooltipOpenId === id) {
        this.hoveredId = id;
        this.lockClickId = id;
        setTimeout(() => {
          if (this.lockClickId === id) this.lockClickId = null;
        }, 250);
        this.$nextTick(() => this.applyTooltipPlacement(id));
      }
    },
    onTriggerKeydown(e, id) {
      if (e.key === "Escape") {
        this.tooltipOpenId = null;
        this.hoveredId = null;
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggleTooltip(id);
      }
    },
    // se vuoi mantenere queste API:
    showTooltip(id) {
      this.onCellEnter(id);
    },
    hideTooltip() {
      // opzionale: se chiamata da blur del bottone
      const id = this.tooltipOpenId ?? this.hoveredId;
      this.onCellLeave(id);
    },
    handleDocumentClick(e) {
      // chiudi se clicco fuori da qualsiasi .client-cell
      if (!e.target.closest(".client-cell")) {
        this.tooltipOpenId = null;
      }
    },
    async copyToClipboard(text, kind = null) {
      const value = String(text ?? "").trim();

      // Se il tipo non è passato, prova a inferirlo
      if (!kind) {
        if (/^\+?[\d()\s.-]{5,}$/.test(value)) kind = "phone";
        else if (/@/.test(value)) kind = "email"; // PEC se vuoi, vedi sotto
        else kind = "text";
      }

      try {
        await navigator.clipboard.writeText(value);

        const msgMap = {
          phone: "Numero di telefono copiato",
          email: "Email copiata",
          pec: "PEC copiata",
          text: "Testo copiato",
        };

        this.$toast?.success?.(`${msgMap[kind] || msgMap.text} negli appunti`);
      } catch {
        this.$toast?.error?.("Impossibile copiare negli appunti");
      }
    },

    safeDocKey(doc, i) {
      if (doc && doc.id != null) return "doc-" + String(doc.id);
      if (doc && doc.url) return "url-" + doc.url;
      if (doc && doc.name) return "name-" + doc.name;
      return "idx-" + String(i);
    },

    formatToLocalDate,
    formatToLocalDateAndHours,
    formatPriceToPrint,

    formatDocName(name) {
      return (
        (name ?? "")
          .toString()
          // prima togli eventuale ".dev" immediatamente prima di ".pdf"
          .replace(/\.dev(?=\.pdf$)/i, "")
          // poi rimuovi "_N_<numero>.pdf" in fondo
          .replace(/_N_\d+\.pdf$/i, "")
      );
    },

    async openDocumentsModal(quote, section = "pdf") {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.docModalSection = section;
      this.selectedDocuments = [];
      this.docSearchTerm = "";
      this.showDocumentsModal = true;
      const quoteDocs = (quote.documents || []).filter(Boolean).map((d) => ({
        ...d,
        _type: "quote",
        _quoteUpdatedAt: quote.updatedAt ?? null,
      }));
      let ddtDocs = [];
      if (quote.id) {
        try {
          const { data } = await axiosInstance.get("/ddt", {
            params: { quoteId: quote.id },
          });
          ddtDocs = (data || [])
            .filter((d) => d.documentUrl)
            .map((d) => ({
              id: `ddt-${d.id}`,
              name: `DDT_N_${d.id}.pdf`,
              url: d.documentUrl,
              createdAt: d.date ?? d.createdAt,
              _type: "ddt",
              _causale: d.causale,
              _ddtId: d.id,
            }));
        } catch {
          /* ignora errori fetch DDT */
        }
      }
      this.selectedDocuments = quoteDocs;
      this.selectedDdtDocuments = ddtDocs;
    },
    closeDocumentsModal() {
      this.showDocumentsModal = false;
      this.docModalSection = "pdf";
      this.selectedDocuments = [];
      this.selectedDdtDocuments = [];
      this.currentQuoteId = null;
      this.selectedQuote = null;
      this.docSearchTerm = "";
    },

    /* ----- ARCHIVIAZIONE / RIPRISTINO con modale ----- */
    openArchiveModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showArchiveModal = true;
    },
    closeArchiveModal() {
      if (this.archiving) return;
      this.showArchiveModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmArchive() {
      if (!this.selectedQuote) return;
      try {
        this.archiving = true;
        let response;
        if (this.$store._actions?.archiveQuote) {
          response = await this.archiveQuote(this.selectedQuote.id);
        } else {
          response = await this.$axios.patch(
            `quote/archive/${this.selectedQuote.id}`,
            JSON.stringify({ archived: true })
          );
        }
        this.successMessage =
          response.data?.message || "Preventivo archiviato con successo.";
        this.$toast.success(this.successMessage);

        this.showArchiveModal = false;
        this.currentQuoteId = null;

        await this.refreshArchivedFlag();
        // await this.fetchQuotes({
        //   page: this.pagination.currentPage,
        //   limit: 4,
        //   search: this.searchTerm,
        //   archived: this.showArchived,
        // });
        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.errorMessage =
          err.response?.data?.details ||
          "Impossibile archiviare il preventivo.";
        this.$toast.error(this.errorMessage);
      } finally {
        this.archiving = false;
        this.selectedQuote = null;
      }
    },

    openRestoreModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showRestoreModal = true;
    },
    openDiscountModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showDiscountModal = true;
      this.docSearchTerm = ""; // reset ricerca
      this.selectedDocuments = quote.documents || [];
    },
    closeDiscountModal() {
      if (this.restoring) return;
      this.showDiscountModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
      this.wizardStep = 1;
      this.modifierType = "";
      this.discountPercent = 0;
      this.surchargePercent = 0;
      this.depositAmount = 0;
      this.selectedDocuments = [];
      this.docSearchTerm = "";
    },
    closeRestoreModal() {
      if (this.restoring) return;
      this.showRestoreModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmRestore() {
      if (!this.selectedQuote) return;
      try {
        this.restoring = true;
        let response;
        if (this.$store._actions?.restoreQuote) {
          response = await this.restoreQuote(this.selectedQuote.id);
        } else {
          response = await this.$axios.patch(
            `quote/archive/${this.selectedQuote.id}`,
            JSON.stringify({ isArchived: false })
          );
        }
        this.successMessage =
          response.data?.message || "Preventivo ripristinato con successo.";
        this.$toast.success(this.successMessage);

        this.showRestoreModal = false;
        this.currentQuoteId = null;

        await this.refreshArchivedFlag();

        if (this.showArchived && !this.hasArchived) {
          this.showArchived = false;
          await this.getQuotes(1); // carica la prima pagina degli attivi
          return; // evita ulteriori fetch sulla vista archiviati
        }
        // await this.fetchQuotes({
        //   page: this.pagination.currentPage,
        //   limit: 4,
        //   search: this.searchTerm,
        //   archived: this.showArchived,
        //   isConfirmed: this.filterConfirmed,
        // });
        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.errorMessage =
          err.response?.data?.message ||
          err.response?.data?.details ||
          "Impossibile ripristinare il preventivo.";
        this.$toast.error(this.errorMessage);
      } finally {
        this.restoring = false;
        this.selectedQuote = null;
      }
    },

    /* ----- Ricerca/paginazione ----- */
    async onSearch() {
      this.isLoading = true;
      try {
        this.currentPage = 1;
        await this.fetchQuotes({
          page: 1,
          limit: 7,
          search: this.searchTerm,
          archived: this.showArchived,
          isConfirmed: this.filterConfirmed,
        });
      } catch (err) {
        if (err?.response?.status !== 404) {
          const msg =
            err?.response?.data?.message ||
            err?.response?.data?.details ||
            "Errore nel caricamento dei preventivi.";
          this.errorMessage = msg;
          this.$toast.error(msg);
        }
      } finally {
        this.isLoading = false;
      }
    },

    async onResetSearch() {
      this.isLoading = true;
      try {
        this.currentPage = 1;
        this.searchTerm = "";
        await this.fetchQuotes({
          page: 1,
          limit: 7,
          archived: this.showArchived,
        });
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.details ||
          "Errore durante il reset della ricerca.";
        this.errorMessage = msg;
        this.$toast.error(msg);
      } finally {
        this.isLoading = false;
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.getQuotes(page);
    },

    async getQuotes(page, search = this.searchTerm) {
      this.isLoading = true;
      try {
        const res = await this.fetchQuotes({
          page,
          limit: 7,
          search,
          archived: this.showArchived,
          isConfirmed: this.filterConfirmed,
        });
        await this.refreshArchivedFlag();
        return res;
      } catch (error) {
        if (error?.response?.status === 404 && page !== 1) {
          // prova la prima pagina
          try {
            await this.fetchQuotes({
              page: 1,
              limit: 7,
              search,
              archived: this.showArchived,
              isConfirmed: this.filterConfirmed,
            });
            this.pagination.currentPage = 1;
            return;
          } catch (_) {
            /* ignora, gestiamo sotto */
          }
        }
        if (error?.response?.status !== 404) {
          this.$nextTick(() => {
            this.errorMessage =
              error?.response?.data?.message ||
              error?.response?.data?.details ||
              "Errore nel caricamento dei preventivi.";
            this.$toast.error(this.errorMessage);
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async onToggleArchived(e) {
      this.currentPage = 1;
      await this.getQuotes(1);
      await this.refreshArchivedFlag();

      // chiude subito il tooltip 'sticky' perchè rimuove il focus
      if (e && e.target && typeof e.target.blur === "function") {
        e.target.blur();
      }
    },

    async refreshArchivedFlag() {
      this.hasArchived = await this.hasArchivedQuotes();
    },

    async applyModifier(quote) {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      if (!this.modifierType)
        return this.$toast.error("Seleziona Sconto o Acconto");

      this.confirmBusy = true;
      try {
        if (this.modifierType === "discount_percent") {
          const base = Number(quote.totalPrice) || 0;
          const deposit = Number(quote.deposit) || 0;
          const taxable = Math.max(0, base - deposit); // <-- NOVITÀ
          const percent = Number(this.discountPercent || 0);
          if (!(percent > 0 && percent <= 100)) {
            this.confirmBusy = false;
            return this.$toast.error("Percentuale non valida (0–100).");
          }

          const amount = round2(taxable * (percent / 100)); // <-- NOVITÀ

          const { data } = await axiosInstance.post(
            `quote/priceAdjustment/${quote.id}`,
            { type: "discount_percent", percent, amount }
          );

          // aggiorna UI locale
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) {
            q.discount_percent = percent;
            q.discount = amount; // ora è lo sconto su (totale - acconto)
          }

          this.$toast.success(data?.message || "Sconto aggiornato.");
          this.discountPercent = 0;
        } else if (this.modifierType === "surcharge_percent") {
          const percent = Number(this.surchargePercent || 0);
          if (!(percent > 0)) {
            this.confirmBusy = false;
            return this.$toast.error("Percentuale rincaro non valida.");
          }

          const base = Number(quote.totalPrice) || 0;
          const amount =
            Math.round((base * (percent / 100) + Number.EPSILON) * 100) / 100;

          const { data } = await axiosInstance.post(
            `quote/priceAdjustment/${quote.id}`,
            { type: "surcharge_percent", percent, amount }
          );

          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) {
            q.surcharge_percent = percent;
            q.surcharge = amount;
            q.realTotalPrice =
              Math.round(
                (Number(q.totalPrice || 0) + amount + Number.EPSILON) * 100
              ) / 100;
          }

          this.$toast.success(data?.message || "Rincaro aggiornato.");
          this.surchargePercent = 0;
        } else if (this.modifierType === "deposit") {
          const amount = Number(this.depositAmount || 0);
          if (!(amount > 0)) {
            this.confirmBusy = false;
            return this.$toast.error("Inserisci un acconto valido.");
          }

          const { data } = await axiosInstance.post(
            `quote/priceAdjustment/${quote.id}`,
            { type: "deposit", amount }
          );

          // ✅ aggiorna la riga locale in lista
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) q.deposit = amount;

          this.$toast.success(data?.message || "Acconto aggiornato.");
          this.depositAmount = 0;
        }

        // azzera scelta radio
        this.modifierType = "";

        const qLocal = this.allQuotes.find((x) => x.id === quote.id);

        const needsFetch = !qLocal?.quote_items?.some((qi) => qi?.item);
        const dataForPdf = needsFetch
          ? await this.getQuotebyId(quote.id)
          : qLocal;

        await generateQuotePdf({
          quote: dataForPdf,
          toast: this.$toast,
          uploadQuoteDocument: (payload) =>
            this.$store.dispatch("uploadQuoteDocument", payload),
          onComplete: async () => {
            await this.getQuotes(this.pagination.currentPage);
          },
        });
        this.closeDiscountModal();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nell’applicazione."
        );
      } finally {
        this.confirmBusy = false;
      }
    },

    async getQuotebyId(quoteId) {
      try {
        const response = await axiosInstance.get(`/quote/${quoteId}`);
        // console.log("quote by id", response);
        if (response.status === 200) return response.data.quote;
        throw new Error("Errore durante il recupero del preventivo.");
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          "Impossibile caricare i dati del preventivo.";
        this.$toast.error(this.errorMessage);
      }
    },

    onQuoteStateChange(quote, field, newValue) {
      // salva cosa stai cambiando
      this.pendingChange = { id: quote.id, field, newValue };

      const qid = quote?.id ?? "—"; // 👈 ID del preventivo
      const clientName = quote?.Client?.clientName ?? "cliente";

      if (field === "isConfirmed" && newValue === true) {
        this.confirmTitle = "Conferma preventivo";
        this.confirmMessage =
          `Confermare il preventivo <strong>#${qid}</strong> di ` +
          `<strong>${clientName}</strong>?`;
        this.confirmLabel = "Conferma";
        this.confirmBusyLabel = "Confermo…";
      } else if (field === "payment_state" && newValue === "Pagato") {
        const warn =
          quote.state === "Consegnato"
            ? " Confermando <strong>Pagato</strong> non potrai più modificarlo e verrà archiviato dagli attivi."
            : "";
        this.confirmTitle = "Conferma pagamento";
        this.confirmMessage =
          `Impostare il pagamento su <strong>Pagato</strong> per il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?${warn}`;
        this.confirmLabel = "Stato => Pagato";
        this.confirmBusyLabel = "Imposto…";
      } else if (field === "state" && newValue === "In consegna") {
        this.confirmTitle = "Manda in consegna";
        this.confirmMessage =
          `Mandare in consegna il preventivo <strong>#${qid}</strong> di ` +
          `<strong>${clientName}</strong>?<br>` +
          `Questa azione scaricherà lo stock degli articoli.`;
        this.confirmLabel = "Manda in consegna";
        this.confirmBusyLabel = "Aggiorno…";
      } else if (field === "state" && newValue === "Consegnato") {
        const warn =
          quote.payment_state === "Pagato"
            ? " Confermando <strong>Consegnato</strong> non potrai più modificarlo e verrà archiviato dagli attivi."
            : "";
        this.confirmTitle = "Conferma consegna";
        this.confirmMessage =
          `Segnare come <strong>Consegnato</strong> il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?${warn}`;
        this.confirmLabel = "Stato => Consegnato";
        this.confirmBusyLabel = "Aggiorno…";
      } else {
        this.confirmTitle = "Conferma modifica";
        this.confirmMessage =
          `Confermare la modifica dello stato per il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?`;
        this.confirmLabel = "Conferma";
        this.confirmBusyLabel = "Aggiorno…";
      }

      this.showConfirmModal = true;
    },

    handleConfirmCancel() {
      // Ripristina i dati UI ricaricando la pagina corrente (più semplice e sicuro)
      this.showConfirmModal = false;
      this.pendingChange = null;
      this.getQuotes(this.pagination.currentPage);
    },

    async handleConfirmOK() {
      if (!this.pendingChange) return;
      const { id, newValue } = this.pendingChange;

      this.confirmBusy = true;
      try {
        await this.updateQuoteState(id, newValue);
      } finally {
        this.confirmBusy = false;
        this.showConfirmModal = false;
        this.pendingChange = null;
      }
    },

    async updateQuoteState(quoteId, newState) {
      this.successMessage = "";
      this.errorMessage = "";
      // const quote = await this.getQuotebyId(quoteId);

      try {
        // if (quote.payment_state === "Pagato" && newState === "Consegnato") {
        //   const ok = window.confirm(
        //     "Il preventivo è già stato pagato. Confermando 'Consegnato' non potrai più modificarlo e sparirà dagli attivi. Procedere?"
        //   );
        //   if (!ok) return this.getQuotes();
        // }
        // if (quote.state === "Consegnato" && newState === "Pagato") {
        //   const ok = window.confirm(
        //     "Il preventivo è già stato consegnato. Confermando 'Pagato' non potrai più modificarlo e sparirà dagli attivi. Procedere?"
        //   );
        //   if (!ok) return this.getQuotes();
        // }

        // const ok = window.confirm(
        //   "Sei sicuro di voler confermare questo preventivo?"
        // );
        // if (!ok) return this.getQuotes();

        const payload = JSON.stringify({ state: newState });
        const response = await this.$axios.patch(
          `quote/state/${quoteId}`,
          payload
        );

        if (response.status === 200 || response.status === 201) {
          if (newState === true) {
            this.successMessage =
              response.data.message ||
              "Il preventivo è stato confermato con successo.";
            this.$toast.success(this.successMessage);
          } else if (newState === "Consegnato") {
            this.successMessage =
              response.data.message || "Stato aggiornato a 'Consegnato'.";
            this.$toast.success(this.successMessage);
          } else if (newState === "Pagato") {
            this.successMessage =
              response.data.message || "Stato aggiornato a 'Pagato'.";
            this.$toast.success(this.successMessage);
          } else if (newState === "In consegna") {
            this.successMessage =
              response.data?.message || "Stato aggiornato a 'In consegna'.";
            this.$toast.success(this.successMessage);
          }

          const q = this.allQuotes.find((q) => q.id === quoteId);
          if (q) {
            if (newState === "Pagato") {
              q.payment_state = newState;
            } else if (
              newState === "Consegnato" ||
              newState === "In consegna"
            ) {
              q.state = newState;
            } else {
              q.isConfirmed = newState;
            }
            if (response.data?.quote?.Client?.lastQuote !== undefined) {
              q.lastQuote = response.data.quote.Client.lastQuote;
            }
          }

          if (q?.payment_state === "Pagato" && q?.state === "Consegnato") {
            setTimeout(async () => {
              // await this.fetchQuotes({
              //   page: this.pagination.currentPage,
              //   limit: 4,
              //   search: this.searchTerm,
              // });
              await this.refreshArchivedFlag();
              await this.getQuotes(this.pagination.currentPage);
              this.successMessage =
                "Stato aggiornato e preventivo archiviato con successo.";
              this.$toast.success(this.successMessage);
            }, 1000);
          }
        }
      } catch (error) {
        const status = error.response?.status;
        const data = error.response?.data ?? {};

        if (status === 409 && data.code === "STOCK_INSUFFICIENT") {
          this.errorMessage =
            data.message || "Stock insufficiente per uno o più articoli.";
        } else if (status === 409) {
          this.errorMessage =
            data.details ||
            data.message ||
            `Lo stato del preventivo con ID '${quoteId}' è già '${newState}'`;
        } else {
          this.errorMessage =
            data.details || data.error || "Errore durante l'aggiornamento.";
        }

        this.$toast.error(this.errorMessage);
        this.getQuotes();
      }
    },

    /* ---- utilità stato preventivo ---- */
    isCompleted(quote) {
      // consideriamo "completato" quando è Pagato + Consegnato
      return quote?.payment_state === "Pagato" && quote?.state === "Consegnato";
    },

    canRestore(quote) {
      // negli archiviati non mostriamo il ripristino se è già completato
      return !this.isCompleted(quote);
    },

    canEdit(quote) {
      // se sto guardando gli archiviati e il preventivo è completato => niente modifica
      return !(
        this.showArchived &&
        (this.canRestore(quote) || this.isCompleted(quote))
      );
    },
  },

  watch: {
    $route(to, from) {
      const toName = to?.name;
      const fromName = from?.name; // può essere undefined al primo giro
      if (toName === "quotes" && fromName !== "quotes") {
        this.getQuotes(this.currentPage);
      }
    },
    searchTerm() {
      this.onSearch();
    },
    // "$route.name"(toName) {
    //   if (toName === "quotes") this.getQuotes(this.currentPage);
    // },
  },

  async mounted() {
    await this.refreshArchivedFlag();
    this.getQuotes(this.currentPage);

    document.addEventListener("click", this.handleDocumentClick, {
      passive: true,
    });
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
};
</script>

<style src="@/css/QuotePage.css"></style>
